import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner, faCheckCircle, faExclamationTriangle,
  faRedo, faInfoCircle, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

interface ExecutionTrackerProps {
  executionId: string;
  clusterId: string;
  authToken: string | null;
  onComplete?: () => void;
  onResume?: () => void;
}

interface ExecutionStep {
  index: number;
  description: string;
  command?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  startedAt?: string;
  completedAt?: string;
  failedAt?: string;
  error?: {
    stepIndex: number;
    message: string;
    isPermissionError: boolean;
    isRetryable: boolean;
  };
  result?: any;
}

interface ExecutionState {
  id: string;
  status: 'pending' | 'running' | 'resuming' | 'completed' | 'failed';
  steps: ExecutionStep[];
  currentStepIndex: number;
  canRetry: boolean;
  canResume: boolean;
  resumeInstructions?: {
    message: string;
    requiredPermissions: string[];
    resumeFromStep: number;
  };
  progress: number;
}

export const ExecutionTracker: React.FC<ExecutionTrackerProps> = ({
  executionId,
  clusterId,
  authToken,
  onComplete,
  onResume
}) => {
  const [execution, setExecution] = useState<ExecutionState | null>(null);
  const [loading, setLoading] = useState(true);
  const [resuming, setResuming] = useState(false);

  const fetchExecutionStatus = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/execution-status/${executionId}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setExecution(data.execution);
        setLoading(false);

        // Call onComplete callback if execution is done
        if (data.execution.status === 'completed' && onComplete) {
          onComplete();
        }
      }
    } catch (error) {
      console.error('Error fetching execution status:', error);
      setLoading(false);
    }
  }, [clusterId, executionId, authToken, onComplete]);

  useEffect(() => {
    if (!executionId) return;

    // Poll for execution status
    const pollInterval = setInterval(() => {
      fetchExecutionStatus();
    }, 2000); // Poll every 2 seconds

    fetchExecutionStatus(); // Initial fetch

    return () => clearInterval(pollInterval);
  }, [executionId, fetchExecutionStatus]);

  const handleResume = async () => {
    setResuming(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/accounts/eks/clusters/${clusterId}/resume-execution/${executionId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Execute the solution again with resume=true
        if (data.instructions && onResume) {
          onResume();
        }
      }
    } catch (error) {
      console.error('Error resuming execution:', error);
    } finally {
      setResuming(false);
    }
  };

  if (loading || !execution) {
    return (
      <div className="bg-white border border-gray-200 rounded p-3">
        <div className="flex items-center space-x-2 text-xs text-gray-600">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
          <span>Loading execution status...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded p-3 space-y-3">
      {/* Header with Progress */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-gray-900">Execution Progress</h3>
        <div className="flex items-center space-x-2">
          {execution.status === 'completed' && (
            <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
          )}
          {execution.status === 'failed' && (
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
          )}
          {(execution.status === 'running' || execution.status === 'resuming') && (
            <FontAwesomeIcon icon={faSpinner} className="text-blue-600 animate-spin" />
          )}
          <span className="text-sm font-bold text-blue-600">{execution.progress}%</span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all duration-500 ${
            execution.status === 'completed' ? 'bg-green-600' :
            execution.status === 'failed' ? 'bg-red-600' :
            'bg-blue-600'
          }`}
          style={{ width: `${execution.progress}%` }}
        ></div>
      </div>

      {/* Steps List */}
      <div className="space-y-2">
        <div className="text-xs font-semibold text-gray-700">Steps:</div>
        {execution.steps.map((step, idx) => (
          <div
            key={idx}
            className={`flex items-start space-x-2 p-2 rounded text-xs ${
              step.status === 'completed' ? 'bg-green-50 border border-green-200' :
              step.status === 'failed' ? 'bg-red-50 border border-red-200' :
              step.status === 'in_progress' ? 'bg-blue-50 border border-blue-200' :
              'bg-gray-50 border border-gray-200'
            }`}
          >
            <div className="mt-0.5">
              {step.status === 'completed' && (
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
              )}
              {step.status === 'failed' && (
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
              )}
              {step.status === 'in_progress' && (
                <FontAwesomeIcon icon={faSpinner} className="text-blue-600 animate-spin" />
              )}
              {step.status === 'pending' && (
                <div className="w-3 h-3 rounded-full border-2 border-gray-300"></div>
              )}
            </div>
            <div className="flex-1">
              <div className={`font-medium ${
                step.status === 'completed' ? 'text-green-900' :
                step.status === 'failed' ? 'text-red-900' :
                step.status === 'in_progress' ? 'text-blue-900' :
                'text-gray-600'
              }`}>
                Step {idx + 1}: {step.description}
              </div>
              {step.command && (
                <div className="mt-1 text-xs font-mono text-gray-600 bg-gray-100 p-1 rounded">
                  $ {step.command}
                </div>
              )}
              {step.error && (
                <div className="mt-1 text-xs text-red-700">
                  Error: {step.error.message}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Resume Instructions */}
      {execution.canResume && execution.resumeInstructions && (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 rounded">
          <div className="flex items-start space-x-2">
            <FontAwesomeIcon icon={faShieldAlt} className="text-yellow-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-xs font-bold text-yellow-900 mb-1">
                Permission Error Detected
              </h4>
              <p className="text-xs text-yellow-800 mb-2">
                {execution.resumeInstructions.message}
              </p>

              {execution.resumeInstructions.requiredPermissions.length > 0 && (
                <>
                  <div className="text-xs font-semibold text-yellow-900 mb-1">
                    Required Permissions:
                  </div>
                  <ul className="text-xs text-yellow-800 space-y-0.5 mb-3 list-disc list-inside">
                    {execution.resumeInstructions.requiredPermissions.map((perm, idx) => (
                      <li key={idx}>{perm}</li>
                    ))}
                  </ul>
                </>
              )}

              <button
                onClick={handleResume}
                disabled={resuming}
                className={`px-3 py-1.5 text-xs rounded transition-colors ${
                  resuming
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-yellow-600 text-white hover:bg-yellow-700'
                }`}
              >
                <FontAwesomeIcon icon={resuming ? faSpinner : faRedo} className={`mr-1 ${resuming ? 'animate-spin' : ''}`} />
                {resuming ? 'Resuming...' : 'Resume Execution'}
              </button>

              <p className="text-xs text-yellow-700 mt-2">
                <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                Add the required permissions to your IAM role, then click "Resume" to continue from where it failed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Retry Option */}
      {execution.canRetry && !execution.canResume && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
          <div className="flex items-start space-x-2">
            <FontAwesomeIcon icon={faRedo} className="text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-xs font-bold text-blue-900 mb-1">
                Retryable Error
              </h4>
              <p className="text-xs text-blue-800 mb-2">
                This execution failed due to a temporary error. You can retry the operation.
              </p>
              <button
                onClick={handleResume}
                disabled={resuming}
                className={`px-3 py-1.5 text-xs rounded transition-colors ${
                  resuming
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <FontAwesomeIcon icon={resuming ? faSpinner : faRedo} className={`mr-1 ${resuming ? 'animate-spin' : ''}`} />
                {resuming ? 'Retrying...' : 'Retry Execution'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Completion Message */}
      {execution.status === 'completed' && (
        <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded">
          <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
            <div className="text-xs font-bold text-green-900">
              Execution completed successfully!
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
